import { useCallback, useMemo } from 'react';

import { TranslationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useTranslation as useTranslationCore } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/types';
import { AppHelper, LoggerHelper } from '@helpers';
import { useSearchParamsStore, useTranslationsStore } from '@store';

import { useApi } from '../useApi';
import { useURLSearchParams } from '../useURLSearchParams';

const BCP_IDP_GENERIC = 'BCPIDPGENERIC';

export const useTranslation = () => {
	const t = useTranslationCore();
	const { contentsClient: fetchContentsClient } = useApi();
	const { searchParams } = useURLSearchParams();
	const clientIdStore = useSearchParamsStore((state) => state.clientId);
	const { translations, setState: setTranslations } = useTranslationsStore();
	const { clientId: clientIdSearchParams } = searchParams;

	const fetchContents = useCallback(
		async (language: Language) => {
			try {
				const clientId = clientIdSearchParams || clientIdStore;
				let result = translations || {};

				if (!clientId) {
					throw new Error('No clientId specified in searchParams.');
				}

				const res = await Promise.all([
					fetchContentsClient({
						clientId: BCP_IDP_GENERIC,
						language: language.toUpperCase(),
					}),
					fetchContentsClient({
						clientId,
						language: language.toUpperCase(),
					}),
				]);

				/* throw error if no content in any of the lists */
				if (!res[0]?.data?.contents?.length || !res[1]?.data?.contents?.length) {
					throw new Error(
						`No contents available. (${BCP_IDP_GENERIC} length - ${JSON.stringify(
							res[0].data?.contents?.length
						)}) (${clientId} length - ${JSON.stringify(res[1].data?.contents?.length)})`
					);
				}

				/* join both generic contents, and specific clientId contents, into a single object */
				result = res.reduce<Record<string, string>>((accumulator, current) => {
					const transform = TranslationHelper.transformArrayToObject(current.data?.contents || [], {
						propNameKey: 'title',
						propNameValue: 'description',
					});

					return { ...accumulator, ...transform };
				}, {});

				/* store the result */
				setTranslations({ translations: result });

				TranslationHelper.register(
					'globalI18n',
					{
						default: { bcp: language === Language.pt ? result : {}, ab: {} },
						pt: { bcp: language === Language.pt ? result : {}, ab: {} },
						en: { bcp: language === Language.en ? result : {}, ab: {} },
					},
					{ addAndReplace: true }
				);

				TranslationHelper.changeLanguage(language || Language.pt);

				return Promise.resolve(result);
			} catch (err) {
				const catchError = AppHelper.convertCatchError(err);

				LoggerHelper.error('App', 'useTranslation > init [catch]:', catchError, { overlay: { show: true } });
				return Promise.reject(err);
			}
		},
		[clientIdSearchParams, clientIdStore, translations, fetchContentsClient, setTranslations]
	);

	return useMemo(
		() => ({
			fetchContents,
			t,
		}),
		[t, fetchContents]
	);
};
